import React, { useMemo } from 'react';
import './PathVisualization.css';

/**
 * Path visualization — renders the Steiner tree as a horizontal strip
 * of poster cards with branching. Preserved pipeline from v1:
 * tokens -> parseTree -> flatten with sourceRef -> render.
 */

export default function PathVisualization({ result, controller }) {
  const items = useMemo(() => {
    if (!result || !controller?.board?.cy) return [];

    const { tokens, allNodes } = result;
    const cy = controller.board.cy;
    const targetNodeIds = controller._targetNodeIds;

    // Stage 1: Parse tokens into tree
    const parseTree = (toks, startIdx = 0) => {
      const items = [];
      let i = startIdx;
      let pendingConn = null;

      while (i < toks.length) {
        const t = toks[i];
        if (t.type === 'group-end') return { items, endIdx: i + 1 };
        if (t.type === 'node') {
          const cyId = allNodes[t.idx];
          const el = cy.getElementById(cyId);
          const d = el.length ? el.data() : null;
          if (d) {
            items.push({
              type: 'card', cyId, label: d.label, image: d.image,
              isActor: d.id.startsWith('a'), isTv: d.id.startsWith('t'),
              isMovie: d.id.startsWith('m'), isTarget: targetNodeIds.has(d.id),
              connector: pendingConn,
            });
          }
          pendingConn = null;
          i++;
        } else if (t.type === 'arrow') { pendingConn = 'arrow'; i++; }
        else if (t.type === 'plus') { pendingConn = 'plus'; i++; }
        else if (t.type === 'group-start') {
          const branches = [];
          i++;
          const first = parseTree(toks, i);
          branches.push(first.items);
          i = first.endIdx;
          while (i < toks.length && toks[i]?.type === 'plus'
            && (i + 1) < toks.length && toks[i + 1]?.type === 'group-start') {
            i += 2;
            const next = parseTree(toks, i);
            branches.push(next.items);
            i = next.endIdx;
          }
          items.push({ type: 'fork', branches, connector: pendingConn });
          pendingConn = null;
        } else { i++; }
      }
      return { items, endIdx: i };
    };

    // Stage 2: Find fork source
    const findForkSource = (items, forkIdx) => {
      const fork = items[forkIdx];
      if (!fork || fork.type !== 'fork') return null;
      if (fork.connector !== 'plus') {
        for (let i = forkIdx - 1; i >= 0; i--) {
          if (items[i].type === 'card') return items[i];
        }
        return null;
      }
      for (let i = forkIdx - 1; i >= 0; i--) {
        if (items[i].type !== 'card') continue;
        if (items[i].connector === 'plus') continue;
        for (let j = i - 1; j >= 0; j--) {
          if (items[j].type === 'card') return items[j];
        }
        return null;
      }
      return null;
    };

    // Stage 3: Count branch nodes
    const branchNodeCount = (branchItems) => {
      let count = 0;
      for (const it of branchItems) {
        if (it.type === 'card') count++;
        else if (it.type === 'fork') {
          for (const b of it.branches) count += branchNodeCount(b);
        }
      }
      return count;
    };

    // Stage 4: Flatten tree
    const flattenTree = (items, depth = 0) => {
      const flat = [];
      for (let ii = 0; ii < items.length; ii++) {
        const item = items[ii];
        if (item.type === 'card') {
          flat.push({ ...item, branchDepth: depth });
        } else if (item.type === 'fork') {
          const forkSource = findForkSource(items, ii);
          for (let bi = 0; bi < item.branches.length; bi++) {
            const branchItems = flattenTree(item.branches[bi], depth + 1);
            if (bi === 0) {
              if (branchItems.length > 0) {
                branchItems[0].connector = item.connector || 'arrow';
              }
            } else {
              const needsRef = item.branches.slice(0, bi).some(
                b => branchNodeCount(b) > 1
              );
              if (needsRef && forkSource && branchItems.length > 0) {
                branchItems[0].connector = 'plus';
                branchItems[0].sourceRef = {
                  image: forkSource.image || '',
                  label: forkSource.label || '',
                };
              } else {
                if (branchItems.length > 0) branchItems[0].connector = 'plus';
              }
            }
            flat.push(...branchItems);
          }
        }
      }
      return flat;
    };

    const tree = parseTree(tokens);
    return flattenTree(tree.items);
  }, [result, controller]);

  if (items.length === 0) return null;

  return (
    <div className="wm-path-wrap">
      <div className="wm-path">
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {/* Connector */}
            {i > 0 && item.connector && (
              item.sourceRef ? (
                <div className="wm-path-conn wm-conn-ref">
                  <span className="wm-conn-ref-plus">+</span>
                  {item.sourceRef.image && (
                    <img
                      className="wm-conn-ref-img"
                      src={item.sourceRef.image}
                      alt={item.sourceRef.label}
                      loading="lazy"
                    />
                  )}
                  <span className="wm-conn-ref-arrow">&rsaquo;</span>
                </div>
              ) : (
                <div className={`wm-path-conn ${item.connector === 'plus' ? 'wm-conn-plus' : ''}`}>
                  {item.connector === 'plus' ? '+' : '\u203A'}
                </div>
              )
            )}

            {/* Card */}
            <div className={`wm-step ${item.isActor ? 'wm-step-actor' : item.isTv ? 'wm-step-tv' : 'wm-step-movie'} ${item.isTarget ? 'wm-step-target' : ''}`}>
              {item.image && (
                <img
                  className="wm-step-img"
                  src={item.image}
                  alt={item.label}
                  loading="lazy"
                />
              )}
              <span className="wm-step-label">{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
